import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type EntityType = "comunidad" | "grupo" | "peña";

export interface Group {
  id: string;
  name: string;
  description: string | null;
  logo_url: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  entity_type: EntityType;
  visibility: string;
  max_members: number | null;
  location: string | null;
  rules: string | null;
  features: string[];
}

export interface GroupMember {
  id: string;
  group_id: string;
  user_id: string;
  role: "admin" | "tesorero" | "miembro";
  joined_at: string;
  // joined from profile
  display_name?: string;
  avatar_url?: string;
}

export interface GroupInvitation {
  id: string;
  group_id: string;
  invited_email: string;
  invited_by: string;
  status: string;
  created_at: string;
  responded_at: string | null;
}

export function useGroups() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("groups" as any)
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching groups:", error);
    } else {
      setGroups((data as any) || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const createGroup = async (name: string, description?: string, logoFile?: File, entityType: EntityType = "grupo", options?: { visibility?: string; max_members?: number | null; location?: string }) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error("Debes iniciar sesión"); return null; }

    let logo_url: string | null = null;

    if (logoFile) {
      const ext = logoFile.name.split(".").pop();
      const path = `${user.id}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("group-logos")
        .upload(path, logoFile, { upsert: true });

      if (uploadError) {
        toast.error("Error subiendo logo");
        console.error(uploadError);
      } else {
        const { data: urlData } = supabase.storage.from("group-logos").getPublicUrl(path);
        logo_url = urlData.publicUrl;
      }
    }

    // Create group
    const { data: group, error } = await supabase
      .from("groups" as any)
      .insert({ name, description: description || null, logo_url, created_by: user.id, entity_type: entityType, visibility: options?.visibility || "private", max_members: options?.max_members || null, location: options?.location || null } as any)
      .select()
      .single();

    if (error) {
      toast.error("Error creando grupo");
      console.error(error);
      return null;
    }

    // Add creator as admin
    await supabase
      .from("group_members" as any)
      .insert({ group_id: (group as any).id, user_id: user.id, role: "admin" } as any);

    toast.success("¡Grupo creado!");
    await fetchGroups();
    return group as any as Group;
  };

  return { groups, loading, createGroup, refetch: fetchGroups };
}

export function useGroupDetail(groupId: string | undefined) {
  const [group, setGroup] = useState<Group | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [invitations, setInvitations] = useState<GroupInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);

  const fetchGroup = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);

    const [groupRes, membersRes, invitationsRes] = await Promise.all([
      supabase.from("groups" as any).select("*").eq("id", groupId).single(),
      supabase.from("group_members" as any).select("*").eq("group_id", groupId),
      supabase.from("group_invitations" as any).select("*").eq("group_id", groupId),
    ]);

    if (groupRes.data) setGroup(groupRes.data as any);
    
    const rawMembers = (membersRes.data as any) || [];
    // Fetch profiles for members
    if (rawMembers.length > 0) {
      const userIds = rawMembers.map((m: any) => m.user_id);
      const { data: profiles } = await supabase.rpc("get_all_public_profiles");
      const profileMap = new Map((profiles || []).map((p: any) => [p.user_id, p]));

      const enriched = rawMembers.map((m: any) => {
        const profile = profileMap.get(m.user_id);
        return {
          ...m,
          display_name: profile?.display_name || "Piloto",
          avatar_url: profile?.avatar_url || null,
        };
      });
      setMembers(enriched);

      // Current user role
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const myMembership = enriched.find((m: any) => m.user_id === user.id);
        setCurrentUserRole(myMembership?.role || null);
      }
    }

    setInvitations((invitationsRes.data as any) || []);
    setLoading(false);
  }, [groupId]);

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  const inviteMember = async (email: string) => {
    if (!groupId) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from("group_invitations" as any)
      .insert({ group_id: groupId, invited_email: email, invited_by: user.id } as any);

    if (error) {
      if (error.message.includes("duplicate")) {
        toast.error("Ya existe una invitación para este email");
      } else {
        toast.error("Error enviando invitación");
        console.error(error);
      }
      return;
    }

    // Send invitation email via edge function
    try {
      const { data: profile } = await supabase
        .from("profiles" as any)
        .select("display_name")
        .eq("user_id", user.id)
        .single();

      await supabase.functions.invoke("send-invitation", {
        body: {
          email,
          group_id: groupId,
          group_name: group?.name || "",
          inviter_name: (profile as any)?.display_name || "Un piloto",
        },
      });
    } catch (e) {
      console.warn("No se pudo enviar email de invitación:", e);
    }

    toast.success(`Invitación enviada a ${email}`);
    await fetchGroup();
  };

  const updateMemberRole = async (memberId: string, newRole: string) => {
    const { error } = await supabase
      .from("group_members" as any)
      .update({ role: newRole } as any)
      .eq("id", memberId);

    if (error) {
      toast.error("Error actualizando rol");
      console.error(error);
      return;
    }
    toast.success("Rol actualizado");
    await fetchGroup();
  };

  const removeMember = async (memberId: string) => {
    const { error } = await supabase
      .from("group_members" as any)
      .delete()
      .eq("id", memberId);

    if (error) {
      toast.error("Error eliminando miembro");
      return;
    }
    toast.success("Miembro eliminado");
    await fetchGroup();
  };

  const updateGroup = async (updates: { name?: string; description?: string; logo_url?: string }) => {
    if (!groupId) return;
    const { error } = await supabase
      .from("groups" as any)
      .update(updates as any)
      .eq("id", groupId);

    if (error) {
      toast.error("Error actualizando grupo");
      return;
    }
    toast.success("Grupo actualizado");
    await fetchGroup();
  };

  return {
    group, members, invitations, loading, currentUserRole,
    inviteMember, updateMemberRole, removeMember, updateGroup, refetch: fetchGroup,
  };
}

export function useMyInvitations() {
  const [invitations, setInvitations] = useState<(GroupInvitation & { group_name?: string })[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchInvitations = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("group_invitations" as any)
      .select("*")
      .eq("status", "pending");

    if (!error && data) {
      // Get group names
      const groupIds = [...new Set((data as any[]).map(i => i.group_id))];
      if (groupIds.length > 0) {
        const { data: groupsData } = await supabase
          .from("groups" as any)
          .select("id, name")
          .in("id", groupIds);

        const groupMap = new Map((groupsData as any[] || []).map(g => [g.id, g.name]));
        setInvitations((data as any[]).map(i => ({ ...i, group_name: groupMap.get(i.group_id) })));
      } else {
        setInvitations([]);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchInvitations(); }, [fetchInvitations]);

  const acceptInvitation = async (invitation: GroupInvitation) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Add as member
    const { error: memberError } = await supabase
      .from("group_members" as any)
      .insert({ group_id: invitation.group_id, user_id: user.id, role: "miembro" } as any);

    if (memberError) {
      toast.error("Error uniéndote al grupo");
      console.error(memberError);
      return;
    }

    // Update invitation status
    await supabase
      .from("group_invitations" as any)
      .update({ status: "accepted", responded_at: new Date().toISOString() } as any)
      .eq("id", invitation.id);

    toast.success("¡Te has unido al grupo!");
    await fetchInvitations();
  };

  const declineInvitation = async (invitationId: string) => {
    await supabase
      .from("group_invitations" as any)
      .update({ status: "declined", responded_at: new Date().toISOString() } as any)
      .eq("id", invitationId);

    toast.info("Invitación rechazada");
    await fetchInvitations();
  };

  return { invitations, loading, acceptInvitation, declineInvitation, refetch: fetchInvitations };
}
